import { Component, OnInit, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs';
import { LocalizationModule } from '@abp/ng.core';
import { ThemeSharedModule, ToasterService } from '@abp/ng.theme.shared';
import { NgbModal, NgbModalModule, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ImpersonationService } from '../../services/impersonation.service';
import { ImpersonatableUserDto } from '../../models/impersonation.models';

@Component({
  selector: 'app-impersonation-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, LocalizationModule, ThemeSharedModule, NgbModalModule],
  template: `
    <ng-template #modalTemplate let-modal>
      <div class="modal-header">
        <h5 class="modal-title">{{ '::Impersonation:Title' | abpLocalization }}</h5>
        <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
      </div>
      <div class="modal-body">
        <div class="mb-3">
          <input
            type="text"
            class="form-control"
            [placeholder]="'::Impersonation:SearchPlaceholder' | abpLocalization"
            [(ngModel)]="searchTerm"
            (ngModelChange)="onSearchChange($event)"
          />
        </div>

        <p class="text-muted small mb-3">{{ '::Impersonation:SelectUser' | abpLocalization }}</p>

        <div *ngIf="loading" class="text-center py-4">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>

        <div *ngIf="!loading && filteredUsers.length === 0" class="text-center py-4 text-muted">
          {{ '::Impersonation:NoUsersFound' | abpLocalization }}
        </div>

        <div *ngIf="!loading && filteredUsers.length > 0" class="user-list">
          <div
            *ngFor="let user of filteredUsers"
            class="user-item d-flex align-items-center justify-content-between p-3 border-bottom"
            [class.selected]="selectedUser?.id === user.id"
            (click)="selectUser(user)"
          >
            <div class="user-info">
              <div class="fw-semibold">{{ user.fullName || user.name + ' ' + user.surname }}</div>
              <div class="text-muted small">{{ user.email }}</div>
              <div class="text-muted small">
                <span class="badge bg-secondary me-1" *ngFor="let role of user.roles">{{ role }}</span>
              </div>
            </div>
            <div *ngIf="selectedUser?.id === user.id" class="text-primary">
              <i class="fas fa-check-circle"></i>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" (click)="modal.dismiss()">
          {{ '::Impersonation:Cancel' | abpLocalization }}
        </button>
        <button
          type="button"
          class="btn btn-primary"
          [disabled]="!selectedUser || submitting"
          (click)="startImpersonation()"
        >
          <span *ngIf="submitting" class="spinner-border spinner-border-sm me-1"></span>
          {{ '::Impersonation:StartButton' | abpLocalization }}
        </button>
      </div>
    </ng-template>
  `,
  styles: [`
    .user-list {
      max-height: 400px;
      overflow-y: auto;
    }
    .user-item {
      cursor: pointer;
      transition: background-color 0.2s;
    }
    .user-item:hover {
      background-color: rgba(0, 0, 0, 0.05);
    }
    .user-item.selected {
      background-color: rgba(var(--bs-primary-rgb), 0.1);
    }
  `],
})
export class ImpersonationModalComponent implements OnInit, OnDestroy {
  @ViewChild('modalTemplate') modalTemplate!: TemplateRef<any>;

  users: ImpersonatableUserDto[] = [];
  filteredUsers: ImpersonatableUserDto[] = [];
  selectedUser: ImpersonatableUserDto | null = null;
  searchTerm = '';
  loading = false;
  submitting = false;

  private modalRef: NgbModalRef | null = null;
  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  constructor(
    private impersonationService: ImpersonationService,
    private modalService: NgbModal,
    private toaster: ToasterService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(term => {
        this.filterUsers(term);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  open(): void {
    this.loading = true;
    this.selectedUser = null;
    this.searchTerm = '';

    this.impersonationService.getImpersonatableUsers().subscribe({
      next: result => {
        this.users = result.items;
        this.filteredUsers = result.items;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toaster.error('::Impersonation:Error:NotAllowed');
      },
    });

    this.modalRef = this.modalService.open(this.modalTemplate, {
      size: 'lg',
      centered: true,
    });
  }

  close(): void {
    this.modalRef?.close();
    this.modalRef = null;
  }

  onSearchChange(term: string): void {
    this.searchSubject.next(term);
  }

  private filterUsers(term: string): void {
    if (!term) {
      this.filteredUsers = this.users;
      return;
    }

    const lowerTerm = term.toLowerCase();
    this.filteredUsers = this.users.filter(
      user =>
        user.name?.toLowerCase().includes(lowerTerm) ||
        user.surname?.toLowerCase().includes(lowerTerm) ||
        user.email?.toLowerCase().includes(lowerTerm) ||
        user.userName?.toLowerCase().includes(lowerTerm) ||
        user.fullName?.toLowerCase().includes(lowerTerm)
    );
  }

  selectUser(user: ImpersonatableUserDto): void {
    this.selectedUser = this.selectedUser?.id === user.id ? null : user;
  }

  startImpersonation(): void {
    if (!this.selectedUser) return;

    this.submitting = true;
    this.impersonationService.startImpersonation(this.selectedUser.id).subscribe({
      next: result => {
        this.submitting = false;
        if (result.success) {
          this.toaster.success('::Impersonation:Started');
          this.close();
          this.router.navigate(['/']);
        } else {
          this.toaster.error(result.errorMessage || '::Impersonation:Error:NotAllowed');
        }
      },
      error: err => {
        this.submitting = false;
        const errorMessage = err.error?.error?.message || '::Impersonation:Error:NotAllowed';
        this.toaster.error(errorMessage);
      },
    });
  }
}
